import { useEffect, useState } from "react";
import {
  Share, Mail, Maximize2, Minimize2, CheckCircle2, Loader2, RotateCcw, FileText, AlertTriangle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import ProposalEmailFlow from "./ProposalEmailFlow";
import IncompleteLinesDialog from "./IncompleteLinesDialog";
import { estApi } from "../lib/api";

// Every layout lists the scopes and their items; the mode only decides where money shows.
const MODES = [
  { value: "by_section", label: "Scope sums" },
  { value: "itemized", label: "Unit price per item" },
  { value: "lump_sum", label: "Total bid only" },
];

function Block({ title, children }) {
  return (
    <section className="rounded-lg border border-slate-200 bg-white p-3">
      <h3 className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
        {title}
      </h3>
      {children}
    </section>
  );
}

function TextBlock({ label, value, onSave, rows = 5, testid, onReset, overridden }) {
  const [draft, setDraft] = useState(value || "");
  useEffect(() => { setDraft(value || ""); }, [value]);
  return (
    <div>
      <div className="flex items-center justify-between">
        <label className="text-[11px] text-slate-500">{label}</label>
        {overridden && (
          <button onClick={onReset} className="text-[10px] text-[#007BFF] hover:underline"
                  data-testid={`${testid}-reset`}>
            <RotateCcw className="mr-0.5 inline h-3 w-3" /> use default
          </button>
        )}
      </div>
      <textarea value={draft} rows={rows} onChange={(e) => setDraft(e.target.value)}
                onBlur={() => draft !== (value || "") && onSave(draft)}
                className="mt-1 w-full rounded border border-slate-200 px-2 py-1.5 text-xs leading-relaxed focus:border-[#007BFF] focus:outline-none"
                data-testid={testid} />
    </div>
  );
}

function SwitchRow({ label, testid, checked, overridden, onChange, onReset }) {
  return (
    <div className="mt-1 flex items-center justify-between gap-2">
      <span className="text-[11px] text-slate-500">{label}</span>
      <span className="flex items-center gap-1.5">
        {overridden && (
          <button onClick={onReset} title="Follow the Settings default again"
                  className="text-[10px] text-slate-400 underline hover:text-slate-600"
                  data-testid={`${testid}-reset`}>
            default
          </button>
        )}
        <Switch checked={checked} data-testid={testid} onCheckedChange={onChange} />
      </span>
    </div>
  );
}

export default function ProposalTab({ estimate, onStatusChange, expanded = false, onToggleExpand }) {
  const id = estimate.id;
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [log, setLog] = useState([]);

  const load = () => estApi.getProposal(id).then(setData);
  useEffect(() => {
    load();
    estApi.getProposalEmailLog(id).then(setLog).catch(() => {});
    // eslint-disable-next-line
  }, [id]);

  const save = (patch) => estApi.saveProposal(id, patch).then(setData)
    .catch((e) => toast.error(e?.response?.data?.detail || "Could not save"));

  const exportPdf = async () => {
    setBusy(true);
    try {
      const { version } = await estApi.createProposalVersion(id);
      await estApi.downloadProposalPdf(id, version.version);
      // Exporting is the submission, so the estimate's status moves with it.
      const fresh = await estApi.getProposal(id);
      setData(fresh);
      if (fresh.status !== estimate.status) onStatusChange?.(fresh.status);
      toast.success(`PDF v${version.version} downloaded${
        fresh.status !== estimate.status ? ` · status moved to ${fresh.status}` : ""}`);
    } catch {
      toast.error("PDF generation failed");
    } finally {
      setBusy(false);
    }
  };

  const [blocked, setBlocked] = useState(null);

  const markSent = async () => {
    try {
      const res = await estApi.markProposalSent(id);
      toast.success(`Marked as ${res.status}`);
      onStatusChange?.(res.status);
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail?.code === "incomplete_lines") setBlocked(detail);
      else toast.error(typeof detail === "string" ? detail : "Could not mark as sent");
    }
  };

  if (!data) {
    return (
      <div className="flex items-center justify-center py-16 text-sm text-slate-500">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Building proposal…
      </div>
    );
  }

  const r = data.resolved;
  const ov = data.overrides || {};

  return (
    <div className="flex items-start gap-4" data-testid="proposal-tab">
      <div className="min-w-0 flex-1">
        <div className="mb-2 flex items-center gap-2">
          <Button variant="data" size="sm" className="text-xs" onClick={exportPdf} disabled={busy}
                  data-testid="proposal-export-btn">
            {busy ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                  : <Share className="mr-1.5 h-3.5 w-3.5" />} PDF
          </Button>
          <Button variant="data" size="sm" className="text-xs" onClick={() => setEmailOpen(true)}
                  data-testid="proposal-email-btn">
            <Mail className="mr-1.5 h-3.5 w-3.5" /> Proposal
          </Button>
          <Button size="sm" className="text-xs" onClick={markSent}
                  data-testid="proposal-mark-sent-btn">
            <CheckCircle2 className="mr-1.5 h-3.5 w-3.5" /> Mark Sent
          </Button>
          {data.stale && (
            <span className="flex items-center gap-1 text-[11px] text-amber-600"
                  data-testid="proposal-stale-warning">
              <AlertTriangle className="h-3 w-3" /> estimate changed since the last PDF
            </span>
          )}
          {onToggleExpand && (
            <Button variant="nav" size="sm" className="ml-auto h-8 w-8 p-0" onClick={onToggleExpand} title={expanded ? "Exit full screen" : "Full screen"}
                    aria-label={expanded ? "Exit full screen" : "Full screen"} data-testid="proposal-expand-btn">
              {expanded ? <Minimize2 className="h-3.5 w-3.5" /> : <Maximize2 className="h-3.5 w-3.5" />}
            </Button>)}
        </div>

        <iframe title="Proposal preview" srcDoc={data.html}
                className="h-[1180px] w-full rounded-lg border border-slate-300 bg-white shadow-sm"
                data-testid="proposal-preview-frame" />
      </div>

      <div className="w-[330px] shrink-0 space-y-3 pb-24">
        <Block title="Pricing display">
          <p className="mb-1.5 text-[10px] leading-snug text-slate-400">
            Scopes and item quantities always show. This picks how much pricing detail goes with them.
          </p>
          <select value={r.pricing_display_mode}
                  onChange={(e) => save({ pricing_display_mode: e.target.value })}
                  className="h-8 w-full rounded border border-slate-200 bg-white px-2 text-xs"
                  data-testid="proposal-display-mode">
            {MODES.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
          </select>
          <SwitchRow label="Show item numbers" testid="proposal-show-item-numbers" checked={r.show_item_numbers !== false}
                     overridden={ov.show_item_numbers !== undefined}
                     onChange={(v) => save({ show_item_numbers: v })}
                     onReset={() => save({ show_item_numbers: null })} />
          <SwitchRow label="Show locations under each item" testid="proposal-show-locations" checked={r.show_locations !== false}
                     overridden={ov.show_locations !== undefined}
                     onChange={(v) => save({ show_locations: v })}
                     onReset={() => save({ show_locations: null })} />
          <SwitchRow label="Show sales tax line" testid="proposal-show-tax" checked={r.show_tax !== false}
                     overridden={ov.show_tax !== undefined}
                     onChange={(v) => save({ show_tax: v })}
                     onReset={() => save({ show_tax: null })} />
          <SwitchRow label="Show Textura fee line" testid="proposal-show-textura" checked={!!r.show_textura}
                     overridden={ov.show_textura !== undefined}
                     onChange={(v) => save({ show_textura: v })}
                     onReset={() => save({ show_textura: null })} />
          <SwitchRow label="Show deposits" testid="proposal-show-deposits" checked={r.show_deposits !== false}
                     overridden={ov.show_deposits !== undefined}
                     onChange={(v) => save({ show_deposits: v })}
                     onReset={() => save({ show_deposits: null })} />
          <SwitchRow label="Show alternates separately" testid="proposal-show-alternates" checked={r.show_alternates !== false}
                     overridden={ov.show_alternates !== undefined}
                     onChange={(v) => save({ show_alternates: v })}
                     onReset={() => save({ show_alternates: null })} />
          <label className="mt-1 flex items-center justify-between">
            <span className="text-[11px] text-slate-500">
              Include qualifications ({data.qualification_count})
              {(data.sanity_counts?.red || data.sanity_counts?.amber) ? (
                <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-800" data-testid="proposal-sanity-count"
                      title="Estimate sanity flags — see the Estimate tab">
                  {data.sanity_counts.red} red · {data.sanity_counts.amber} amber
                </span>) : null}
            </span>
            <Switch checked={!r.exclude_qualifications} data-testid="proposal-include-quals"
                    onCheckedChange={(v) => save({ exclude_qualifications: !v })} />
          </label>
          <div className="mt-2 flex gap-2">
            <div className="flex-1">
              <label className="text-[11px] text-slate-500">Valid for (days)</label>
              <Input type="number" key={`vd-${r.valid_through_days}`}
                     defaultValue={r.valid_through_days} className="h-8 text-xs"
                     onBlur={(e) => save({ valid_through_days: parseInt(e.target.value, 10) || 30 })}
                     data-testid="proposal-validity-days" />
            </div>
            <div className="flex-1">
              <label className="text-[11px] text-slate-500">Proposal #</label>
              <Input key={`pn-${r.proposal_number}`} defaultValue={r.proposal_number}
                     className="h-8 text-xs"
                     onBlur={(e) => save({ proposal_number: e.target.value })}
                     data-testid="proposal-number" />
            </div>
          </div>
        </Block>

        <Block title="Content">
          <div>
            <TextBlock label="Terms" value={r.terms} rows={6}
                       testid="proposal-terms" overridden={!!ov.terms}
                       onSave={(v) => save({ terms: v })}
                       onReset={() => save({ terms: "" })} />
          </div>
          <p className="mt-1.5 text-[10px] leading-snug text-slate-400">
            Edits here live on the proposal only — the estimate lines and the Settings defaults are
            untouched.
          </p>
        </Block>

        <Block title="Signature">
          <Input key={`sn-${r.signature_name}`} defaultValue={r.signature_name} placeholder="Name"
                 className="h-8 text-xs"
                 onBlur={(e) => save({ signature_name: e.target.value })}
                 data-testid="proposal-signature-name" />
          <Input key={`st-${r.signature_title}`} defaultValue={r.signature_title} placeholder="Title"
                 className="mt-1.5 h-8 text-xs"
                 onBlur={(e) => save({ signature_title: e.target.value })}
                 data-testid="proposal-signature-title" />
        </Block>

        <Block title="Generated versions">
          {data.versions.length === 0 && (
            <p className="text-[11px] text-slate-400">No PDF generated yet.</p>
          )}
          <div className="space-y-1" data-testid="proposal-versions">
            {data.versions.map((v) => (
              <button key={v.version}
                      onClick={() => estApi.downloadProposalPdf(id, v.version)}
                      className="flex w-full items-center gap-2 rounded px-1.5 py-1 text-left text-[11px] hover:bg-slate-50"
                      data-testid={`proposal-version-${v.version}`}>
                <FileText className="h-3 w-3 text-slate-400" />
                <span className="font-medium text-slate-700">v{v.version}</span>
                <span className="text-slate-400">
                  {new Date(v.created_at).toLocaleString()}
                </span>
                <span className="ml-auto tabular-nums text-slate-500">
                  ${Number(v.total_sales || 0).toLocaleString()}
                </span>
              </button>
            ))}
          </div>
        </Block>

        <Block title="Send history">
          {log.length === 0 && <p className="text-[11px] text-slate-400">Not sent yet.</p>}
          <div className="space-y-1" data-testid="proposal-send-log">
            {log.map((l) => (
              <div key={l.id} className="rounded bg-slate-50 px-2 py-1 text-[11px]">
                <div className={l.ok ? "text-slate-700" : "text-red-600"}>
                  {l.ok ? "Sent" : "Failed"} · v{l.pdf_version} ·{" "}
                  {new Date(l.created_at).toLocaleString()}
                </div>
                <div className="truncate text-slate-400">{(l.to || []).join(", ")}</div>
              </div>
            ))}
          </div>
        </Block>
      </div>

      <IncompleteLinesDialog
        open={!!blocked} onOpenChange={(v) => !v && setBlocked(null)}
        estimateId={id} detail={blocked} onResolved={markSent}
      />

      <ProposalEmailFlow
        open={emailOpen} onOpenChange={setEmailOpen} estimateId={id}
        onSent={(res) => {
          onStatusChange?.(res.status);
          load();
          estApi.getProposalEmailLog(id).then(setLog).catch(() => {});
        }}
      />
    </div>
  );
}
